/**
 * WorkspaceContext — SPA-side hydration of the workspace named by the
 * URL segment `/w/:slug/...`.
 *
 * See docs/users-onboarding-billing-plan.md §1.5.
 *
 * Responsibilities:
 *   - Read `:slug` from URL params.
 *   - Fetch /api/workspaces/:slug (which is 404 for non-members — same
 *     shape as "doesn't exist," see §6.3).
 *   - Expose the workspace + role to descendants via useWorkspace().
 *   - Render a "not found" fallback if the API returns 404.
 *
 * Does NOT do auth gating — assumes RequireAuth is upstream in the tree.
 * Does NOT set up the shell chrome — that's AppShell (which sits inside).
 */

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { StepUpInterstitial } from '@aigovops/design-system';
import { PROFILE_STORAGE_KEY, useOptionalAuth } from './auth';

// PR-Cluster-6-j: 'assessor' added so the SPA can key affordances
// (e.g. Acknowledge dispute on <DisputePanel />) off the session
// role. workspaceContext() on the server synthesises a virtual
// assessor Membership from assessor_engagements — same enum, so
// the client type mirrors the server's MembershipRole faithfully.
export type WorkspaceRole =
  | 'owner'
  | 'admin'
  | 'editor'
  | 'viewer'
  | 'advisor'
  | 'assessor';

export interface WorkspaceSummary {
  workspace_id: string;
  slug: string;
  name: string;
  kind: 'personal' | 'team' | 'consultant';
  primary_domain: string | null;
  domain_verified_at: string | null;
  created_at: string;
  /** Slug of the parent organization, if any. */
  organization_slug: string | null;
}

/**
 * Server-synthesized scope-bar payload for assessor sessions
 * (PR-P0-2b, Systems Architecture §3.5). Emitted by
 * GET /api/workspaces/:slug only when the caller is on an assessor
 * engagement rather than a workspace membership. Regular members see
 * this as undefined; AppShell gates <ScopeBar> render on presence.
 */
export interface ScopeBarPayload {
  engagement_id: string;
  snapshot_id: string;
  engagement_start: string;
  engagement_end: string;
  archive_window_days: number;
  phase: 'active' | 'read_only';
  firm_name: string;
  invited_by_name: string | null;
  invited_at: string;
  /** Framework under assessment, e.g. "SOC 2 Type II". */
  framework_name?: string;
  /** HMAC package hash for signed deliverables (ADR-0010). */
  package_hash?: string;
  /** Package id for the /verify/:package_id verification link. */
  package_id?: string;
  /** Whether the engagement has an active dispute. */
  has_dispute?: boolean;
}

interface WorkspaceContextValue {
  workspace: WorkspaceSummary;
  role: WorkspaceRole;
  scopeBar?: ScopeBarPayload;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      workspace: WorkspaceSummary;
      role: WorkspaceRole;
      scopeBar?: ScopeBarPayload;
    }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string }
  | { kind: 'mfa_required'; workspaceName: string; stepUpUrl: string };

/**
 * Fetches /api/workspaces/:slug, then renders children with context
 * populated. Loading and 404 states get lightweight inline UI —
 * consumers who want branded chrome should compose above this.
 */
export function WorkspaceGate({ children }: { children: ReactNode }) {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    if (!slug) {
      setState({ kind: 'not_found' });
      return;
    }
    setState({ kind: 'loading' });
    (async () => {
      try {
        const res = await fetch(`/api/workspaces/${encodeURIComponent(slug)}`, {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (cancelled) return;
        if (res.status === 404) {
          setState({ kind: 'not_found' });
          return;
        }
        if (!res.ok) {
          setState({ kind: 'error', message: `HTTP ${res.status}` });
          return;
        }
        const body = (await res.json()) as {
          workspace: WorkspaceSummary;
          membership: { role: WorkspaceRole };
          scope_bar?: ScopeBarPayload;
        };
        setState({
          kind: 'ready',
          workspace: body.workspace,
          role: body.membership.role,
          scopeBar: body.scope_bar,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // V101-6-3: the profile is per workspace. When the URL's workspace is not
  // the one the profile was hydrated for, fetch this workspace's own profile
  // (workspace-scoped, membership-gated) and hold the children until it
  // matches — no page may read or write another workspace's profile.
  const auth = useOptionalAuth();
  const profileSlug = auth?.user?.profile_slug;
  const needsProfile = !!auth?.user && state.kind === 'ready' && !!slug && profileSlug !== slug;
  const setProfileId = auth?.setProfileId;
  useEffect(() => {
    if (!needsProfile || !slug || !setProfileId) return;
    let cancelled = false;
    (async () => {
      let pid: string | undefined;
      try {
        const r = await fetch(`/api/w/${encodeURIComponent(slug)}/profile`, { credentials: 'include', headers: { Accept: 'application/json' } });
        if (r.ok) pid = ((await r.json()) as { profile?: { profile_id?: string } | null }).profile?.profile_id ?? undefined;
      } catch { pid = undefined; }
      if (cancelled) return;
      try {
        if (pid) window.localStorage.setItem(PROFILE_STORAGE_KEY, pid);
        else window.localStorage.removeItem(PROFILE_STORAGE_KEY);
      } catch { /* storage unavailable */ }
      setProfileId(pid, slug);
    })();
    return () => { cancelled = true; };
  }, [needsProfile, slug, setProfileId]);

  // eslint-disable-next-line react-hooks/rules-of-hooks -- always called, order stable
  const [searchParams] = useSearchParams();
  const cameFromAggregate = searchParams.get('from') === 'aggregate';

  if (state.kind === 'loading' || needsProfile) {
    return <div style={{ padding: '2rem' }}>Loading workspace…</div>;
  }
  if (state.kind === 'mfa_required') {
    return (
      <StepUpInterstitial
        workspaceName={state.workspaceName}
        stepUpUrl={state.stepUpUrl}
        onCancel={() => window.history.back()}
      />
    );
  }
  if (state.kind === 'not_found') {
    return (
      <div style={{ padding: '2rem', fontFamily: 'var(--font-ui)', color: 'var(--text)' }}>
        <h1 style={{ fontSize: 'var(--font-size-lg)' }}>Workspace not found</h1>
        <p style={{ color: 'var(--text-muted)' }}>
          No such workspace, or you don't have access. If you believe this is a
          mistake, contact the workspace owner.
        </p>
        <p>
          <Link to={cameFromAggregate ? `/w/${slug}/app/alerts?view=aggregate` : '/'} style={{ color: 'var(--secondary)', textDecoration: 'underline' }}>
            {cameFromAggregate ? '← Back to all clients' : '← Home'}
          </Link>
        </p>
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div style={{ padding: '2rem', fontFamily: 'var(--font-ui)', color: 'var(--text)' }}>
        <h1 style={{ fontSize: 'var(--font-size-lg)' }}>Couldn't load workspace</h1>
        <p style={{ color: 'var(--text-muted)' }}>{state.message}</p>
        <p>
          <Link to={cameFromAggregate ? `/w/${slug}/app/alerts?view=aggregate` : '/'} style={{ color: 'var(--secondary)', textDecoration: 'underline' }}>
            {cameFromAggregate ? '← Back to all clients' : '← Home'}
          </Link>
        </p>
      </div>
    );
  }
  return (
    <WorkspaceContext.Provider
      value={{
        workspace: state.workspace,
        role: state.role,
        scopeBar: state.scopeBar,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

/**
 * Read the current workspace + role from context. Throws if called
 * outside a WorkspaceGate — better a loud crash than silently wrong
 * cross-tenant behavior.
 */
export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error(
      'useWorkspace() must be used inside <WorkspaceGate>. Are you rendering ' +
        'a page that lives under /w/:slug/app/*?'
    );
  }
  return ctx;
}

/**
 * Optional variant that returns null instead of throwing — for shared
 * components (nav, footer) that render both inside and outside workspace
 * context.
 */
export function useOptionalWorkspace(): WorkspaceContextValue | null {
  return useContext(WorkspaceContext);
}
